import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';
import { isVideoPlayerNode } from 'components/commercetools-ui/atoms/rich-text/renderers/video-player';

/**
 * Nodes that paint something even when the document carries no text.
 *
 * This list tracks what `RichText` can actually render, not what Contentful can store — a node
 * with no renderer produces an empty fragment, which is the same empty accordion this check
 * exists to prevent. `HR` and the inline nodes are covered (the app renders `HR`; the library's
 * defaults paint a visible placeholder span for the inline ones), and `TABLE` falls through to
 * the library's `<table>`.
 *
 * Deliberately absent: `BLOCKS.EMBEDDED_ASSET`, which has no renderer in `nodes.tsx` and none in
 * the library either, so an asset-only document renders nothing at all.
 */
const STANDALONE_NODE_TYPES = new Set<string>([
  BLOCKS.HR,
  BLOCKS.TABLE,
  INLINES.EMBEDDED_ENTRY,
  INLINES.ASSET_HYPERLINK,
  INLINES.ENTRY_HYPERLINK,
]);

type RichTextNode = { nodeType: string; content?: RichTextNode[] };

const paintsWithoutText = (node: RichTextNode): boolean => {
  if (STANDALONE_NODE_TYPES.has(node.nodeType)) return true;
  // A block-level embedded entry only renders when it is a video player; every other content
  // type returns null from the renderer, so it cannot stand in as content on its own.
  if (node.nodeType === BLOCKS.EMBEDDED_ENTRY) return isVideoPlayerNode(node);
  return (node.content ?? []).some(paintsWithoutText);
};

/**
 * Whether a Contentful rich-text document would render anything visible.
 *
 * A field cleared in Contentful does not come back as `undefined` — it comes back as a document
 * holding one empty paragraph, which is truthy. Rendering on truthiness alone therefore puts an
 * empty accordion section on the PDP for every field an editor left blank.
 *
 * Narrows to `Document` so call sites can pass the value straight to `RichText`. The predicate
 * only carries meaning when true: a `false` result means "nothing to paint", which covers both
 * an absent document and a present-but-empty one.
 */
export const hasRichTextContent = (document?: Document): document is Document => {
  if (!document) return false;
  if (documentToPlainTextString(document).trim().length > 0) return true;
  return paintsWithoutText(document as unknown as RichTextNode);
};
